import last from 'lodash/last';
import merge from 'lodash/merge';

import { DEFAULT_SOURCE, type GasPayload } from '@atlaskit/analytics-gas-types';
import { MEDIA_CONTEXT } from '@atlaskit/analytics-namespaced-context/MediaAnalyticsContext';
import type UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';

import { extractFromEventContext } from '../atlaskit/extract-from-event-context';
import { getComponents } from '../atlaskit/get-components';
import { getExtraAttributes } from '../atlaskit/get-extra-attributes';
import { getPackageHierarchy } from '../atlaskit/get-package-hierarchy';
import { getPackageInfo } from '../atlaskit/get-package-info';
import { getSources } from '../atlaskit/get-sources';

const listenerVersion = process.env._PACKAGE_VERSION_ as string;

function getMediaContexts(event: UIAnalyticsEvent) {
	return extractFromEventContext(MEDIA_CONTEXT, event);
}

function getMediaRegion() {
	try {
		// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- existing usage
		return window && window.sessionStorage && window.sessionStorage.getItem('media-api-region');
	} catch (e) {
		return;
	}
}

export function processEvent(event: UIAnalyticsEvent): GasPayload {
	const sources = getSources(event);
	const { packageName, packageVersion } = last(getPackageInfo(event)) || ({} as any);

	const extraAttributes = getExtraAttributes(event);

	const mediaContexts = getMediaContexts(event) || [];
	const mediaAttributes = merge({}, ...mediaContexts);

	const tags: Set<string> = new Set(event.payload.tags || []);
	tags.add('media');

	const components = getComponents(event);

	const mediaRegion = getMediaRegion();

	const payload = {
		source: last(sources) || DEFAULT_SOURCE,
		actionSubject: event.payload.actionSubject,
		action: event.payload.action,
		eventType: event.payload.eventType,
		actionSubjectId: event.payload.actionSubjectId,
		// for backward compatibilty
		name: event.payload.name,
		tags: Array.from(tags),
		attributes: {
			packageName,
			packageVersion,
			...merge({}, extraAttributes, mediaAttributes, event.payload.attributes),
			componentHierarchy: components.join('.') || undefined,
			packageHierarchy: getPackageHierarchy(event),
			sourceHierarchy: sources.join('.') || undefined,
			listenerVersion,
			...(mediaRegion ? { mediaRegion } : undefined),
		},
		nonPrivacySafeAttributes: event.payload.nonPrivacySafeAttributes,
	};

	return payload;
}
